import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getCCForOverhead,
    getCCOverheadPercent,
    getOverheadConfigGrid,
    saveCCOverheadValues,
} from '../../api/AccountsAPI/overheadPercentageConfigAPI';

// ── Async Thunks ───────────────────────────────────────────────────────────────

/** Cost centres not yet configured with an Overhead percentage */
export const fetchCCForOverhead = createAsyncThunk(
    'overheadPercentageConfig/fetchCCForOverhead',
    async (_, { rejectWithValue }) => {
        try {
            const res = await getCCForOverhead();
            return res.data?.Data || [];
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load available cost centres');
        }
    }
);

/** Grid of cost centres already configured with an Overhead percentage */
export const fetchOverheadConfigGrid = createAsyncThunk(
    'overheadPercentageConfig/fetchOverheadConfigGrid',
    async (_, { rejectWithValue }) => {
        try {
            const res = await getOverheadConfigGrid();
            return res.data?.Data || [];
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load the overhead percentage grid');
        }
    }
);

/** Fetch the current percentage for a cost centre (used before showing an inline edit field) */
export const fetchCCOverheadPercent = createAsyncThunk(
    'overheadPercentageConfig/fetchCCOverheadPercent',
    async (code, { rejectWithValue }) => {
        try {
            const res = await getCCOverheadPercent(code);
            const list = res.data?.Data || [];
            return list[0]?.ccvalue || '0';
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load the current percentage');
        }
    }
);

/**
 * A cost centre's overhead % and its approved DCA budget (spGetCCOverheadpercent returns the budget as `Value`).
 * Used to show what moves to / from CC-12 before an overhead % change or a DCA amendment is saved; the caller
 * keeps the result (dispatch(...).unwrap()), nothing is stored in this slice.
 */
export const fetchCCOverheadInfo = createAsyncThunk(
    'overheadPercentageConfig/fetchCCOverheadInfo',
    async (code, { rejectWithValue }) => {
        try {
            const res = await getCCOverheadPercent(code);
            const row = (res.data?.Data || [])[0] || {};
            return { percent: parseFloat(row.ccvalue) || 0, dcaBudget: parseFloat(row.Value) || 0 };
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load the overhead percentage');
        }
    }
);

/** Save/update the Overhead percentage for a cost centre */
export const submitCCOverheadPercent = createAsyncThunk(
    'overheadPercentageConfig/submitCCOverheadPercent',
    async ({ code, value, Createdby, RoleId, Action }, { rejectWithValue }) => {
        try {
            const res = await saveCCOverheadValues({ CostCenter: code, Value: value, Createdby, Action, RoleId });
            const status = res.data?.Data;
            if (status !== 'Submited') {
                return rejectWithValue(status || 'Failed to save the overhead percentage');
            }
            return { code, value };
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to save the overhead percentage');
        }
    }
);

// ── Slice ──────────────────────────────────────────────────────────────────────

const initialState = {
    availableCCList: [],
    gridList: [],
    saveResult: null,

    loading: {
        available: false,
        grid: false,
        save: false,
    },
    errors: {
        available: null,
        grid: null,
        save: null,
    },
};

const overheadPercentageConfigSlice = createSlice({
    name: 'overheadPercentageConfig',
    initialState,
    reducers: {
        clearOverheadSaveResult: (state) => { state.saveResult = null; state.errors.save = null; },
        resetOverheadPercentageConfig: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCCForOverhead.pending, s => { s.loading.available = true; s.errors.available = null; })
            .addCase(fetchCCForOverhead.fulfilled, (s, a) => { s.loading.available = false; s.availableCCList = a.payload; })
            .addCase(fetchCCForOverhead.rejected, (s, a) => { s.loading.available = false; s.errors.available = a.payload; });

        builder
            .addCase(fetchOverheadConfigGrid.pending, s => { s.loading.grid = true; s.errors.grid = null; })
            .addCase(fetchOverheadConfigGrid.fulfilled, (s, a) => { s.loading.grid = false; s.gridList = a.payload; })
            .addCase(fetchOverheadConfigGrid.rejected, (s, a) => { s.loading.grid = false; s.errors.grid = a.payload; });

        builder
            .addCase(submitCCOverheadPercent.pending, s => { s.loading.save = true; s.errors.save = null; s.saveResult = null; })
            .addCase(submitCCOverheadPercent.fulfilled, (s, a) => { s.loading.save = false; s.saveResult = a.payload; })
            .addCase(submitCCOverheadPercent.rejected, (s, a) => { s.loading.save = false; s.errors.save = a.payload; });
    },
});

export const {
    clearOverheadSaveResult,
    resetOverheadPercentageConfig,
} = overheadPercentageConfigSlice.actions;

// ── Selectors ──────────────────────────────────────────────────────────────────
export const selectAvailableCCList = s => s.overheadPercentageConfig.availableCCList;
export const selectOverheadGridList = s => s.overheadPercentageConfig.gridList;
export const selectOverheadSaveResult = s => s.overheadPercentageConfig.saveResult;

export const selectAvailableLoading = s => s.overheadPercentageConfig.loading.available;
export const selectOverheadGridLoading = s => s.overheadPercentageConfig.loading.grid;
export const selectOverheadSaveLoading = s => s.overheadPercentageConfig.loading.save;
export const selectOverheadGridError = s => s.overheadPercentageConfig.errors.grid;
export const selectOverheadSaveError = s => s.overheadPercentageConfig.errors.save;

export default overheadPercentageConfigSlice.reducer;
